import React from 'react';
import { Bone, SkeletonRegion, range } from '../Skeleton';

// Loading skeletons for the institution dashboard (/institution). Each one
// mirrors the block it stands in for, so the page keeps its shape while data
// loads. Shared primitives live in components/Skeleton.jsx.

const CARD = 'bg-white border border-gray-200 rounded-xl';
const ON_DARK = { background: 'rgba(255,255,255,0.1)' };

/* ── Whole page (first load): sidebar + header + overview ─────────────── */
export function InstitutionShellSkeleton({ sidebar = '#14532d' }) {
  return (
    <SkeletonRegion label="Loading your dashboard…" testId="institution-loading" className="min-h-screen bg-[#F8FAFC] flex">
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-[260px] flex-col px-5 pt-6" style={{ background: sidebar }} aria-hidden="true">
        <Bone className="h-6 w-28 mb-7" style={ON_DARK} />
        <div className="flex items-center gap-3 mb-5">
          <Bone className="w-10 h-10 rounded-lg" style={ON_DARK} />
          <div className="flex-1 space-y-2"><Bone className="h-3 w-32" style={ON_DARK} /><Bone className="h-2.5 w-16" style={ON_DARK} /></div>
        </div>
        <div className="space-y-3 pt-4 border-t border-white/[0.06]">
          {range(10).map(i => <Bone key={i} className="h-5 rounded-lg" style={{ ...ON_DARK, width: `${60 + (i % 3) * 12}%` }} />)}
        </div>
      </aside>
      <div className="flex-1 lg:ml-[260px]">
        <div className="h-[56px] px-5 lg:px-7 bg-white border-b border-gray-200 flex items-center justify-between">
          <Bone className="h-4 w-48" />
          <div className="flex items-center gap-3"><Bone className="h-8 w-40 rounded-lg hidden sm:block" /><Bone className="w-8 h-8 rounded-full" /></div>
        </div>
        <div className="p-6 md:p-8 max-w-6xl space-y-7">
          <div className="space-y-3"><Bone className="h-8 w-64" /><Bone className="h-3.5 w-80 max-w-full" /></div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {range(4).map(i => (
              <div key={i} className={`${CARD} p-5`}>
                <Bone className="w-10 h-10 rounded-lg mb-3" />
                <Bone className="h-7 w-14 mb-2" /><Bone className="h-3 w-24" />
              </div>
            ))}
          </div>
          <div className="grid lg:grid-cols-5 gap-5">
            <div className={`lg:col-span-3 ${CARD} p-6`}>
              <Bone className="h-5 w-40 mb-5" />
              <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                {range(8).map(i => (
                  <div key={i} className="flex items-center gap-3">
                    <Bone className="w-8 h-8 rounded-lg" />
                    <div className="flex-1 space-y-1.5"><Bone className="h-2.5 w-16" /><Bone className="h-3.5 w-24" /></div>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:col-span-2 space-y-4">
              {range(4).map(i => (
                <div key={i} className={`${CARD} p-4 flex items-center gap-4`}>
                  <Bone className="w-10 h-10 rounded-lg" />
                  <div className="flex-1 space-y-1.5"><Bone className="h-3.5 w-32" /><Bone className="h-3 w-44 max-w-full" /></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </SkeletonRegion>
  );
}

/* ── Stat tiles + toolbar + table: Teachers, Students, Licensing,
      Invoices, Devices, Support tickets, Teacher Desk ───────────────── */
function StatTilesBones() {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 flex-shrink-0">
      {range(4).map(i => (
        <div key={i} className={`${CARD} p-4`}>
          <div className="flex items-center justify-between mb-3"><Bone className="w-9 h-9 rounded-xl" /><Bone className="h-3 w-16" /></div>
          <Bone className="h-6 w-14 mb-1.5" /><Bone className="h-3 w-24" />
        </div>
      ))}
    </div>
  );
}

export function StatsTableSkeleton({ label = 'Loading…', testId, heading = false, rows = 8 }) {
  return (
    <SkeletonRegion label={label} testId={testId} className="flex flex-col gap-4 h-[calc(100vh-120px)]">
      {heading && <div className="space-y-2 flex-shrink-0"><Bone className="h-5 w-40" /><Bone className="h-3 w-64 max-w-full" /></div>}
      <StatTilesBones />
      <div className="flex items-center gap-3 flex-shrink-0">
        <Bone className="h-10 w-64 max-w-[60%] rounded-xl" />
        <div className="ml-auto flex gap-2"><Bone className="h-9 w-24 rounded-lg" /><Bone className="h-9 w-24 rounded-lg hidden sm:block" /></div>
      </div>
      <div className={`${CARD} flex-1 min-h-0 overflow-hidden`}>
        <div className="px-5 py-3 border-b border-gray-100 flex gap-6 bg-gray-50/80">
          {range(4).map(i => <Bone key={i} className={`h-3 ${i === 0 ? 'w-32' : 'w-20'}`} />)}
        </div>
        {range(rows).map(i => (
          <div key={i} className="px-5 py-3.5 border-b border-gray-50 flex items-center gap-6">
            <div className="flex items-center gap-3 w-48 max-w-[40%]"><Bone className="w-8 h-8 rounded-full flex-shrink-0" /><Bone className="h-3.5 flex-1" /></div>
            <Bone className="h-3 w-40 max-w-[25%]" />
            <Bone className="h-5 w-16 rounded-full" />
            <Bone className="h-3 w-12 ml-auto" />
          </div>
        ))}
      </div>
    </SkeletonRegion>
  );
}

/* ── Departments & classes ───────────────────────────────────────────── */
export function DeptCardsSkeleton() {
  return (
    <SkeletonRegion label="Loading departments…" testId="departments-loading" className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
      {range(5).map(i => (
        <div key={i} className={`${CARD} p-4 relative overflow-hidden`}>
          <div className="absolute top-0 left-0 w-full h-1 bg-gray-100" />
          <Bone className="w-8 h-8 rounded-lg mb-2.5" />
          <Bone className="h-3.5 w-24 mb-1.5" /><Bone className="h-2.5 w-12 mb-2.5" />
          <div className="flex gap-2"><Bone className="h-3 w-14" /><Bone className="h-3 w-16" /></div>
        </div>
      ))}
    </SkeletonRegion>
  );
}

/** Batch and class cards: big cards in a 3-column grid. */
export function BigCardsSkeleton({ label = 'Loading…', testId, topBar = false }) {
  return (
    <SkeletonRegion label={label} testId={testId} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
      {range(3).map(i => (
        <div key={i} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          {topBar && <div className="h-1.5 w-full bg-gray-100" />}
          <div className="p-6">
            <div className="flex items-start justify-between mb-4"><Bone className="w-12 h-12 rounded-xl" /><Bone className="h-6 w-20 rounded-lg" /></div>
            <Bone className="h-4 w-32 mb-2" /><Bone className="h-3 w-24 mb-4" />
            <div className="flex gap-4 pt-4 border-t border-gray-50"><Bone className="h-3 w-16" /><Bone className="h-3 w-16" /></div>
          </div>
        </div>
      ))}
    </SkeletonRegion>
  );
}

export function StudentTableSkeleton() {
  return (
    <SkeletonRegion label="Loading students…" testId="class-students-loading" className={`${CARD} overflow-hidden`}>
      <div className="px-5 py-3 bg-gray-50/80 border-b border-gray-100 flex gap-16"><Bone className="h-3 w-16" /><Bone className="h-3 w-12" /><Bone className="h-3 w-14 ml-auto" /></div>
      {range(5).map(i => (
        <div key={i} className="px-5 py-3.5 border-b border-gray-50 flex items-center gap-6">
          <div className="flex items-center gap-3 w-48 max-w-[40%]"><Bone className="w-8 h-8 rounded-full flex-shrink-0" /><Bone className="h-3.5 flex-1" /></div>
          <Bone className="h-3 w-44 max-w-[30%]" />
          <Bone className="h-7 w-16 rounded-lg ml-auto" />
        </div>
      ))}
    </SkeletonRegion>
  );
}

/** Teacher search results inside the assign / substitute pickers. */
export function PersonResultsSkeleton() {
  return (
    <SkeletonRegion label="Searching teachers…" testId="teacher-search-loading" className="space-y-1">
      {range(3).map(i => (
        <div key={i} className="flex items-center gap-3 px-3 py-3">
          <Bone className="w-9 h-9 rounded-full flex-shrink-0" />
          <div className="flex-1 space-y-1.5"><Bone className="h-3.5 w-32" /><Bone className="h-3 w-48 max-w-full" /></div>
        </div>
      ))}
    </SkeletonRegion>
  );
}

/** A class's Subjects & Teachers tab. */
export function ClassSubjectsSkeleton() {
  return (
    <SkeletonRegion label="Loading subjects…" testId="class-subjects-loading" className="space-y-4">
      <Bone className="h-8 w-32 rounded-lg" />
      <div className="space-y-3">
        {range(3).map(i => (
          <div key={i} className={`${CARD} p-4 flex items-center gap-3`}>
            <Bone className="w-9 h-9 rounded-lg flex-shrink-0" />
            <div className="flex-1 space-y-1.5"><Bone className="h-3.5 w-28" /><Bone className="h-3 w-48 max-w-full" /></div>
            <Bone className="h-7 w-20 rounded-lg" />
          </div>
        ))}
      </div>
    </SkeletonRegion>
  );
}

/* ── Subjects ────────────────────────────────────────────────────────── */
export function SubjectGridSkeleton() {
  return (
    <SkeletonRegion label="Loading subjects…" testId="subjects-loading">
      <div className="flex items-center justify-between mb-4"><Bone className="h-5 w-24" /><Bone className="h-9 w-32 rounded-lg" /></div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {range(8).map(i => (
          <div key={i} className={`${CARD} p-4`}>
            <Bone className="w-8 h-8 rounded-lg mb-2.5" /><Bone className="h-3.5 w-24 mb-2" /><Bone className="h-3 w-14" />
          </div>
        ))}
      </div>
    </SkeletonRegion>
  );
}

export function SubjectDetailSkeleton() {
  return (
    <SkeletonRegion label="Loading subject…" testId="subject-detail-loading">
      <Bone className="h-4 w-32 mb-4" />
      <div className={`${CARD} p-4 mb-4 flex items-center gap-3`}>
        <Bone className="w-9 h-9 rounded-lg" />
        <div className="space-y-1.5"><Bone className="h-3.5 w-32" /><Bone className="h-3 w-24" /></div>
      </div>
      <div className="space-y-2">
        {range(3).map(i => (
          <div key={i} className={`${CARD} p-3 space-y-1.5`}><Bone className="h-3.5 w-40" /><Bone className="h-3 w-56 max-w-full" /></div>
        ))}
      </div>
    </SkeletonRegion>
  );
}

/* ── DeskeyHUB ───────────────────────────────────────────────────────── */
export function HubOverviewSkeleton() {
  return (
    <SkeletonRegion label="Loading DeskeyHUB…" testId="hub-overview-loading" className="space-y-6">
      <div className="space-y-3"><Bone className="h-8 w-44" /><Bone className="h-3.5 w-80 max-w-full" /></div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {range(4).map(i => (
          <div key={i} className={`${CARD} px-5 py-4 flex items-center gap-4`}>
            <Bone className="w-10 h-10 rounded-lg flex-shrink-0" />
            <div className="space-y-2"><Bone className="h-3 w-20" /><Bone className="h-7 w-12" /></div>
          </div>
        ))}
      </div>
      <div className={`${CARD} p-6 space-y-4`}>
        <div className="flex items-end justify-between gap-6"><div className="space-y-2"><Bone className="h-3 w-24" /><Bone className="h-8 w-40" /></div><Bone className="h-8 w-48 hidden sm:block" /></div>
        <Bone className="h-2 w-full rounded-full" />
      </div>
      <div>
        <Bone className="h-5 w-48 mb-3" />
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {range(3).map(i => (
            <div key={i} className={`${CARD} p-5 flex items-start gap-4`}>
              <Bone className="w-11 h-11 rounded-lg flex-shrink-0" />
              <div className="flex-1 space-y-2"><Bone className="h-4 w-32" /><Bone className="h-3 w-40 max-w-full" /></div>
            </div>
          ))}
        </div>
      </div>
    </SkeletonRegion>
  );
}

export function HubLibrarySkeleton() {
  return (
    <SkeletonRegion label="Loading library…" testId="hub-library-loading" className="space-y-4">
      <div className="flex items-center gap-2">
        <Bone className="w-8 h-8 rounded-lg" />
        <div className="space-y-1.5"><Bone className="h-5 w-48" /><Bone className="h-3 w-64 max-w-full" /></div>
      </div>
      <Bone className="h-9 w-96 max-w-full rounded-lg" />
      <Bone className="h-8 w-80 max-w-full rounded-lg" />
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2.5">
        {range(6).map(i => (
          <div key={i} className={`${CARD} p-3.5 flex items-start gap-3`}>
            <Bone className="w-10 h-10 rounded-xl flex-shrink-0" />
            <div className="flex-1 space-y-1.5"><Bone className="h-3.5 w-28" /><Bone className="h-3 w-36 max-w-full" /></div>
          </div>
        ))}
      </div>
    </SkeletonRegion>
  );
}

export function AccessRowsSkeleton() {
  return (
    <SkeletonRegion label="Loading students…" testId="hub-access-loading" className="space-y-3">
      <Bone className="h-8 w-80 max-w-full rounded-lg" />
      <div className="space-y-1.5">
        {range(6).map(i => (
          <div key={i} className={`${CARD} p-2.5 flex items-center gap-3`}>
            <Bone className="w-8 h-8 rounded-full flex-shrink-0" />
            <div className="flex-1 space-y-1.5"><Bone className="h-3 w-32" /><Bone className="h-2.5 w-44 max-w-full" /></div>
            <Bone className="h-5 w-24 rounded-full" /><Bone className="h-6 w-16 rounded-lg" />
          </div>
        ))}
      </div>
    </SkeletonRegion>
  );
}

/* ── Teacher Desk drill-down ─────────────────────────────────────────── */
/** Lesson list, chapters or topics: stacked rows with an icon and two lines. */
export function DeskRowsSkeleton({ label = 'Loading…', testId, rows = 5 }) {
  return (
    <SkeletonRegion label={label} testId={testId} className="space-y-2 p-1">
      {range(rows).map(i => (
        <div key={i} className={`${CARD} p-3 flex items-center gap-3`}>
          <Bone className="w-9 h-9 rounded-lg flex-shrink-0" />
          <div className="flex-1 space-y-1.5"><Bone className="h-3.5" style={{ width: `${45 + (i % 3) * 12}%` }} /><Bone className="h-3 w-32" /></div>
          <Bone className="h-5 w-16 rounded-full" />
        </div>
      ))}
    </SkeletonRegion>
  );
}

/** Read-only topic preview: framework sections stacked as cards. */
export function TopicPreviewSkeleton() {
  return (
    <SkeletonRegion label="Loading topic…" testId="portfolio-topic-loading" className="space-y-4">
      {range(3).map(i => (
        <div key={i} className={`${CARD} p-5 space-y-2.5`}>
          <Bone className="h-4 w-40" />
          <Bone className="h-3 w-full" /><Bone className="h-3 w-11/12" /><Bone className="h-3 w-3/4" />
        </div>
      ))}
    </SkeletonRegion>
  );
}
